// VariantsMenuBar.tsx
import React, { useState } from "react"
import { useProjectStore } from "stores/projectStore"

import CreateVariantModal from "./CreateVariant/CreateVariantModal.component"
import SelectVariantModal from "./SelectVariant/SelectVariantModal.component"
import TranslationModal from "../Translations/TranslationModal.component"
import LabelsModal from "../Labels/LabelsModal.component"
import PreviewLanguageSelectModal from "../Preview/PreviewLanguageSelectModal.component"
import { Variant, SelectedVariant, EditorRef } from "types/questionnaire.types"

/**
 * Props interface for the VariantsMenuBar component
 */
interface VariantsMenuBarProps {
  /** Function to generate the preview */
  generatePreview: (languages: string[], variant: string, notes?: string[]) => Promise<void>
  /** Function to retrieve the notes for the questionnaire */
  getQuestionnaireNotes?: () => string[]
  /** The currently selected variant object */
  selectedVariant: SelectedVariant
  /** Function to set the selected variant */
  setSelectedVariant: (variant: Variant | { name: string }) => void
  /** Array of variant objects */
  variants: Variant[]
  /** Function to set the array of variants */
  setVariants: (variants: Variant[]) => void
  /** Function to handle the deletion of a variant */
  handleDeleteVariant: (variant: Variant) => Promise<void>
  /** Function to refresh the project data */
  refresh: (id: string) => Promise<void>
  /** Function to insert variant text into the editor */
  insertVariantText: (text: string) => void
  /** Function to control the visibility of the preview mode */
  setPreviewVisible: (visible: boolean) => void
  /** Function to save the questionnaire */
  saveQuestionnaire: (updatedVariants?: Variant[]) => Promise<void>
  /** Ref to the editor component */
  editorRef: React.RefObject<EditorRef | null>
  /** Function to scroll to a specific line in the editor */
  scrollToLine?: (lineNumber: number) => void
  /** Controls visibility of the Labels Modal */
  isLabelsModalVisible: boolean
  /** Callback when Labels Modal visibility changes */
  onLabelsModalVisibilityChange: (visible: boolean) => void
  /** Optional label name to jump to when modal opens */
  labelToJump?: string | null
  /** Callback when jump is complete */
  onJumpComplete?: () => void
  /** Ref to Labels button for tour */
  labelsButtonRef?: React.RefObject<HTMLElement | null>
  /** Whether collaborative mode is enabled */
  collaborativeMode?: boolean
  /** Function to save before switching variants (for collaborative mode) */
  onBeforeVariantSwitch?: () => Promise<void>
  /** Whether the editor is currently connecting to collaboration server */
  isConnecting?: boolean
}

/**
 * `VariantsMenuBar` renders the questionnaire-wide controls of the Editor
 * toolbar's top row: the variant dropdown (switch, New variant…, Manage
 * variants…), then Translations, Metadata and Preview. It has no bar of its
 * own: the Editor's menu bar places it beside File and Insert.
 *
 * @component
 * @param props - The props for the component
 * @returns The rendered VariantsMenuBar component
 */
const VariantsMenuBar: React.FC<VariantsMenuBarProps> = ({
  generatePreview,
  getQuestionnaireNotes,
  selectedVariant,
  setSelectedVariant,
  variants,
  setVariants,
  handleDeleteVariant,
  refresh,
  insertVariantText,
  setPreviewVisible,
  saveQuestionnaire,
  editorRef,
  scrollToLine,
  isLabelsModalVisible,
  onLabelsModalVisibilityChange,
  labelToJump,
  onJumpComplete,
  labelsButtonRef,
  collaborativeMode = false,
  onBeforeVariantSwitch,
  isConnecting = false,
}) => {
  const project = useProjectStore((state) => state.project)
  // "New variant…" in the variant dropdown opens the create modal.
  const [isNewVariantOpen, setIsNewVariantOpen] = useState<boolean>(false)

  return (
    <div className="flex flex-1 items-center gap-1 min-w-0">
      <SelectVariantModal
        project={project}
        variants={variants}
        handleDeleteVariant={handleDeleteVariant}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        refresh={refresh}
        setPreviewVisible={setPreviewVisible}
        onBeforeSwitch={onBeforeVariantSwitch}
        isDisabled={isConnecting}
        onNewVariant={() => setIsNewVariantOpen(true)}
      />
      <CreateVariantModal
        project={project}
        setVariants={setVariants}
        variants={variants}
        selectedVariant={selectedVariant}
        insertVariantText={insertVariantText}
        setSelectedVariant={setSelectedVariant}
        saveQuestionnaire={saveQuestionnaire}
        refresh={refresh}
        collaborativeMode={collaborativeMode}
        isDisabled={isConnecting}
        open={isNewVariantOpen}
        onOpenChange={setIsNewVariantOpen}
      />
      <div className="flex-1" />
      <TranslationModal
        project={project}
        saveQuestionnaire={saveQuestionnaire}
        isDisabled={isConnecting}
      />
      <LabelsModal
        editorRef={editorRef}
        saveQuestionnaire={saveQuestionnaire}
        scrollToLine={scrollToLine}
        jumpToLabel={labelToJump}
        onJumpComplete={onJumpComplete}
        isVisible={isLabelsModalVisible}
        onVisibilityChange={onLabelsModalVisibilityChange}
        buttonRef={labelsButtonRef}
        isDisabled={isConnecting}
        projectId={project.id}
        owner={project.Created_by}
        currentVariant={(selectedVariant as Variant).name || ""}
      />
      <div className="toolbar-divider" />
      <PreviewLanguageSelectModal
        project={project}
        variants={variants}
        selectedVariant={selectedVariant}
        generatePreview={generatePreview}
        isDisabled={isConnecting}
        getQuestionnaireNotes={getQuestionnaireNotes}
      />
    </div>
  )
}

export default VariantsMenuBar
